/**
 * Chunk-load recovery (plan/portal-final/INTERFACES.md section 8). Vite fires
 * `vite:preloadError` when a lazy chunk fails to load, which is what an open
 * tab sees when it asks for a chunk the running bundle names and the network
 * cannot supply. The first failure reloads the page once; a `sessionStorage`
 * flag stops a second reload until the page has run cleanly for
 * `clearAfterMs`, so a broken deployment cannot put the tab in a reload loop.
 */

export const PRELOAD_RELOAD_KEY = 'app-version:preload-reload';
export const PRELOAD_CLEAR_AFTER_MS = 10_000;

interface RecoveryStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface PreloadRecoveryEnv {
  target: {
    addEventListener(type: string, listener: (event: Event) => void): void;
    removeEventListener(type: string, listener: (event: Event) => void): void;
  };
  storage: RecoveryStorage | null;
  reload: () => void;
  setTimeout: (handler: () => void, ms: number) => unknown;
  clearTimeout: (id: unknown) => void;
  clearAfterMs?: number;
}

const installed = new WeakSet<object>();

function read(storage: RecoveryStorage | null): string | null {
  try {
    return storage?.getItem(PRELOAD_RELOAD_KEY) ?? null;
  } catch {
    return null;
  }
}

function write(storage: RecoveryStorage | null, value: string | null) {
  try {
    if (value === null) storage?.removeItem(PRELOAD_RELOAD_KEY);
    else storage?.setItem(PRELOAD_RELOAD_KEY, value);
  } catch {
    // Storage can be unavailable (privacy modes); the reload still runs once per page.
  }
}

/** Installs the listener once per target. Returns the cleanup. */
export function installPreloadRecovery(env: PreloadRecoveryEnv): () => void {
  if (installed.has(env.target)) return () => {};
  installed.add(env.target);
  let reloaded = false;

  const onPreloadError = (event: Event) => {
    if (reloaded || read(env.storage)) return;
    reloaded = true;
    write(env.storage, String(Date.now()));
    event.preventDefault();
    env.reload();
  };
  env.target.addEventListener('vite:preloadError', onPreloadError);
  const timer = env.setTimeout(() => {
    if (!reloaded) write(env.storage, null);
  }, env.clearAfterMs ?? PRELOAD_CLEAR_AFTER_MS);

  return () => {
    env.target.removeEventListener('vite:preloadError', onPreloadError);
    env.clearTimeout(timer);
    installed.delete(env.target);
  };
}

function sessionStorageOrNull(): RecoveryStorage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** The browser wiring, called from main before the app renders. */
export function installBrowserPreloadRecovery(): () => void {
  return installPreloadRecovery({
    target: window,
    storage: sessionStorageOrNull(),
    reload: () => window.location.reload(),
    setTimeout: (handler, ms) => window.setTimeout(handler, ms),
    clearTimeout: (id) => window.clearTimeout(id as number),
  });
}
